import { createRouter, createWebHistory } from 'vue-router';
import { generalSettingsLocation } from '../utils/general-settings.js';
import { laborCostLocation, laborCostSections, legacyLaborCostLocation } from '../utils/labor-cost-navigation.js';
import { useAuthStore } from '../stores/auth.js';
import { applyRouteTheme } from '../utils/theme.js';
import { workspaceMotion } from '../utils/workspace-motion.js';
import { installWorkspaceNavigation } from '../utils/workspace-transitions.js';

const routes = [
  {
    path: '/design-preview/ember',
    name: 'dashboard-design-preview',
    component: () => import('../design-preview/DesignPreview.vue'),
    meta: { title: 'Обзор ресторана · НУР' }
  },
  { path: '/login', name: 'login', component: () => import('../views/LoginView.vue'), meta: { public: true } },
  { path: '/register/:token', name: 'register', component: () => import('../views/InviteView.vue'), meta: { public: true } },
  { path: '/onboard/:token', name: 'onboarding-public', component: () => import('../views/OnboardingView.vue'), meta: { public: true } },
  { path: '/reset-password/:token', name: 'reset-password', component: () => import('../views/InviteView.vue'), meta: { public: true } },
  { path: '/legal/:document?', name: 'legal', component: () => import('../views/LegalView.vue'), meta: { public: true } },
  {
    path: '/feedback/:restaurantId',
    name: 'feedback',
    component: () => import('../views/FeedbackView.vue'),
    meta: { public: true, title: 'Обратная связь' }
  },
  {
    path: '/',
    component: () => import('../layouts/AppShell.vue'),
    children: [
      { path: '', name: 'dashboard', component: () => import('../views/DashboardView.vue') },
      { path: 'chat/:roomId?', name: 'chat', component: () => import('../views/ChatView.vue') },
      { path: 'legal-knowledge', name: 'legal-knowledge', component: () => import('../views/LegalKnowledgeView.vue'), meta: { title: 'База знаний НУР Нейро-Юриста' } },
      { path: 'accounting-knowledge', name: 'accounting-knowledge', component: () => import('../views/AccountingKnowledgeView.vue'), meta: { title: 'База знаний НУР-Бухгалтера' } },
      { path: 'employees', name: 'employees', component: () => import('../views/EmployeesView.vue') },
      { path: 'employees/onboarding', name: 'employee-onboarding', component: () => import('../views/EmployeeOnboardingView.vue'), meta: { viewKey: 'employee-onboarding', permission: 'employees.onboard', title: 'Заявки сотрудников' } },
      { path: 'employees/onboarding/:id', name: 'employee-onboarding-detail', component: () => import('../views/EmployeeOnboardingView.vue'), meta: { viewKey: 'employee-onboarding', permission: 'employees.onboard', title: 'Заявка на оформление' } },
      {
        path: 'employees/:employeeId',
        name: 'employee-detail',
        meta: { title: 'Карточка сотрудника' },
        component: () => import('../views/EmployeeProfileView.vue')
      },
      {
        path: 'employees/:employeeId/settings',
        name: 'employee-settings',
        meta: { title: 'Настройка сотрудника' },
        redirect: (to) => ({
          name: 'employee-detail',
          params: { employeeId: to.params.employeeId },
          query: { ...to.query, edit: '1' }
        })
      },
      { path: 'schedule', redirect: to => legacyLaborCostLocation('/schedule', to.query) },
      { path: 'payroll/cost', redirect: to => legacyLaborCostLocation('/payroll/cost', to.query) },
      { path: 'payroll', redirect: to => legacyLaborCostLocation('/payroll', to.query) },
      {
        path: 'labor-cost', name: 'labor-cost', component: () => import('../views/LaborCostView.vue'),
        meta: { title: 'Labor Cost', viewKey: 'labor-cost' },
        children: [
          { path: 'schedule', name: 'schedule', meta: { laborSection: 'schedule', permissions: ['schedule.self', 'schedule.manage'] }, component: () => import('../views/ScheduleView.vue') },
          { path: 'master', name: 'master-schedule', meta: { laborSection: 'master', permission: 'schedule.manage' }, component: () => import('../views/ScheduleView.vue') },
          { path: 'calculation', name: 'payroll', meta: { laborSection: 'calculation', permission: 'payroll.tab' }, component: () => import('../views/PayrollView.vue') },
          { path: 'operations', name: 'payroll-operations', meta: { laborSection: 'operations', permission: 'payroll.tab' }, component: () => import('../views/PayrollView.vue') },
          { path: 'settings', name: 'labor-cost-settings', meta: { laborSection: 'settings' }, component: () => import('../components/settings/PayrollSettingsPanel.vue') },
          { path: 'cost', name: 'payroll-cost', meta: { laborSection: 'calculation', permission: 'payroll.tab' }, component: () => import('../views/PayrollCostView.vue') }
        ]
      },
      {
        path: 'banking',
        name: 'banking',
        meta: { permission: 'billing.tab', title: 'Банковские счета' },
        component: () => import('../views/BankingView.vue')
      },
      {
        path: 'invoices',
        name: 'supplier-invoices',
        meta: { permission: 'budget.tab', title: 'Счета поставщиков' },
        component: () => import('../views/InvoicesView.vue')
      },
      {
        path: 'counterparties',
        meta: { title: 'Контрагенты', viewKey: 'counterparties-workspace' },
        component: () => import('../views/CounterpartiesWorkspace.vue'),
        children: [
          { path: '', name: 'counterparties', meta: { permission: 'budget.tab' }, component: () => import('../views/CounterpartiesView.vue') },
          { path: 'distribution', name: 'counterparty-distribution', meta: { permission: 'budget.tab' }, component: () => import('../views/ClassificationView.vue'), props: { embedded: true } },
          { path: 'matrix', name: 'counterparty-matrix', meta: { permission: 'budget.tab' }, component: () => import('../views/CounterpartyMatrixView.vue') },
          { path: 'archive/:id?', name: 'legal-counterparties', meta: { permission: 'legal.archive.view', title: 'Юридический архив' }, component: () => import('../views/LegalCounterpartiesView.vue') },
          { path: ':counterpartyId', name: 'counterparty-detail', meta: { permission: 'budget.tab' }, component: () => import('../views/CounterpartiesView.vue') }
        ]
      },
      {
        path: 'invoices/classification',
        name: 'invoice-classification',
        meta: { permission: 'budget.tab' },
        redirect: to => ({ path:'/counterparties/distribution', query:to.query })
      },
      {
        path: 'invoices/:invoiceId',
        name: 'supplier-invoice-detail',
        meta: { permission: 'budget.tab', title: 'Счёт поставщика' },
        component: () => import('../views/InvoicesView.vue')
      },
      {
        path: 'budget',
        name: 'budget',
        meta: { permission: 'budget.tab', title: 'Бюджет' },
        component: () => import('../views/BudgetView.vue')
      },
      { path: 'settings', name: 'settings', component: () => import('../views/SettingsView.vue') },
      {
        path: 'tasks',
        name: 'tasks',
        meta: { viewKey: 'tasks-board' },
        component: () => import('../views/TasksView.vue')
      },
      {
        path: 'tasks/:taskId',
        name: 'task-detail',
        meta: { viewKey: 'tasks-board' },
        component: () => import('../views/TasksView.vue')
      },
      {
        path: 'calendar',
        name: 'calendar',
        component: () => import('../views/TaskCalendarView.vue')
      },
      {
        path: 'journals',
        name: 'journals',
        meta: { permission: 'journals.fill', title: 'Журналы' },
        component: () => import('../views/JournalsView.vue')
      },
      {
        path: 'audit',
        name: 'audit',
        meta: { permission: 'audit.tab', title: 'Санитарный аудит' },
        component: () => import('../views/AuditView.vue')
      },
      {
        path: 'audit/:auditId',
        name: 'audit-detail',
        meta: { permission: 'audit.tab', title: 'Отчёт аудита' },
        component: () => import('../views/AuditView.vue')
      },
      {
        path: 'inventory',
        redirect: '/inventory/balances'
      },
      {
        path: 'inventory/receipts/:receiptId?',
        name: 'inventory-receipts',
        meta: { permission: 'inventory.tab', title: 'Склад', inventorySection: 'receipts', viewKey: 'inventory' },
        component: () => import('../views/InventoryWorkspace.vue')
      },
      {
        path: 'inventory/balances',
        name: 'inventory-balances',
        meta: { permission: 'inventory.tab', title: 'Склад', inventorySection: 'balances', viewKey: 'inventory' },
        component: () => import('../views/InventoryWorkspace.vue')
      },
      {
        path: 'inventory/food-cost',
        name: 'inventory-food-cost',
        meta: { permission: 'inventory.tab', title: 'Food cost', inventorySection: 'food-cost', viewKey: 'inventory' },
        component: () => import('../views/InventoryWorkspace.vue')
      },
      {
        path: 'inventory/stocktakes',
        name: 'inventory-stocktakes',
        redirect: to => ({ path: '/inventory/balances', query: to.query }),
        meta: { permission: 'inventory.tab', title: 'Склад', inventorySection: 'stocktakes' },
        component: () => import('../views/InventoryView.vue')
      },
      {
        path: 'inventory/reconciliation',
        name: 'inventory-reconciliation',
        redirect: to => ({ path: '/inventory/balances', query: to.query }),
        meta: { permission: 'inventory.tab', title: 'Склад', inventorySection: 'reconciliation' },
        component: () => import('../views/InventoryView.vue')
      },
      {
        path: 'inventory/recipes',
        name: 'inventory-recipes',
        redirect: to => ({ path: '/inventory/food-cost', query: to.query }),
        meta: { permission: 'inventory.tab', title: 'Склад', inventorySection: 'recipes' },
        component: () => import('../views/InventoryView.vue')
      },
      {
        path: 'inventory/prices',
        name: 'inventory-prices',
        redirect: to => ({ path: '/inventory/balances', query: to.query }),
        meta: { permission: 'inventory.tab', title: 'Склад', inventorySection: 'prices' },
        component: () => import('../views/InventoryView.vue')
      },
      {
        path: 'inventory/settings',
        name: 'inventory-settings',
        meta: { permission: 'integrations.manage', title: 'Склад', inventorySection: 'settings' },
        component: () => import('../views/InventoryView.vue')
      },
      {
        path: 'vision',
        name: 'vision',
        meta: { permissions: ['vision.self', 'vision.tab'], title: 'Vision' },
        component: () => import('../views/VisionView.vue')
      },
      {
        path: 'vision/lateness',
        name: 'vision-lateness',
        meta: { permissions: ['vision.self', 'vision.tab'], title: 'Статистика опозданий' },
        component: () => import('../views/VisionLatenessView.vue')
      },
      {
        path: 'analytics',
        redirect: '/analytics/revenue'
      },
      {
        path: 'analytics/overview',
        name: 'analytics-overview',
        redirect: '/analytics/revenue'
      },
      {
        path: 'analytics/revenue',
        name: 'analytics-revenue',
        meta: { permission: 'metrics.tab', title: 'Показатели', analyticsSection: 'revenue', viewKey: 'analytics' },
        component: () => import('../views/AnalyticsView.vue')
      },
      {
        path: 'analytics/checks',
        name: 'analytics-checks',
        meta: { permission: 'metrics.tab', title: 'Показатели', analyticsSection: 'checks', viewKey: 'analytics' },
        component: () => import('../views/AnalyticsView.vue')
      },
      {
        path: 'analytics/dishes',
        name: 'analytics-dishes',
        meta: { permission: 'metrics.tab', title: 'Показатели', analyticsSection: 'dishes', viewKey: 'analytics' },
        component: () => import('../views/AnalyticsView.vue')
      },
      {
        path: 'analytics/employees',
        name: 'analytics-employees',
        meta: { permission: 'metrics.tab', title: 'KPI сотрудников', analyticsSection: 'employees', viewKey: 'analytics' },
        component: () => import('../views/EmployeeKpiView.vue')
      },
      {
        path: 'analytics/restaurants',
        name: 'analytics-restaurants',
        redirect: '/analytics/revenue',
        meta: { permission: 'metrics.tab', title: 'Показатели', viewKey: 'analytics' }
      },
      {
        path: 'analytics/temperature/settings',
        name: 'analytics-temperature-settings',
        redirect: { path: '/analytics/settings', query: { focus: 'temperature' } },
        meta: { permission: 'metrics.configure', title: 'Настройки показателей', analyticsSection: 'settings', viewKey: 'analytics' }
      },
      {
        path: 'analytics/temperature',
        name: 'analytics-temperature',
        meta: { permission: 'metrics.tab', title: 'Температура оборудования', analyticsSection: 'temperature', viewKey: 'analytics' },
        component: () => import('../views/TemperatureView.vue')
      },
      {
        path: 'analytics/settings',
        name: 'analytics-settings',
        meta: { permission: 'metrics.configure', title: 'Настройки показателей', analyticsSection: 'settings', viewKey: 'analytics' },
        component: () => import('../views/AnalyticsView.vue')
      },
      {
        path: 'reviews',
        name: 'reviews',
        meta: { permission: 'metrics.tab', title: 'Отзывы', reviewsSection: 'overview', viewKey: 'reviews' },
        component: () => import('../views/ReviewsView.vue')
      },
      {
        path: 'reviews/all',
        name: 'reviews-all',
        meta: { permission: 'metrics.tab', title: 'Все отзывы', reviewsSection: 'list', viewKey: 'reviews-list' },
        component: () => import('../views/ReviewsView.vue')
      },
      {
        path: 'reviews/problems',
        name: 'reviews-problems',
        redirect: '/reviews',
        meta: { permission: 'metrics.tab', title: 'Отзывы', reviewsSection: 'overview', viewKey: 'reviews' }
      },
      {
        path: 'reviews/settings',
        name: 'reviews-settings',
        meta: { permission: 'metrics.configure', title: 'Настройки отзывов', reviewsSection: 'settings', viewKey: 'reviews' },
        component: () => import('../views/ReviewsView.vue')
      }
    ]
  },
  { path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('../views/NotFoundView.vue'), meta: { public: true } }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // Task details are an overlay on the same board, including Back/Forward.
    if (to.meta.viewKey === 'tasks-board' && from.meta.viewKey === 'tasks-board') return false;
    // Route changes must not animate the new page from the previous page's scroll position.
    if (savedPosition) return { ...savedPosition, behavior: 'instant' };
    // Filters, periods and modes are changes inside the current workspace.
    if (to.path === from.path) return false;
    if (to.name === 'employees' && from.name === 'employee-detail') {
      return { top: Number(sessionStorage.getItem('employees:listScroll') || 0), behavior: 'instant' };
    }
    return { top: 0, behavior: 'instant' };
  }
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  await auth.bootstrap();
  if (!to.meta.public && !auth.authenticated) return { name: 'login', query: { next: to.fullPath } };
  if (['legal-knowledge', 'accounting-knowledge'].includes(to.name) && !auth.isAdmin) return { name: 'dashboard' };
  if (to.name === 'login' && auth.authenticated) return { name: 'dashboard' };
  if (to.path === '/settings' && to.query.tab === 'general' && to.query.section === 'payroll') {
    return { ...legacyLaborCostLocation('/settings', to.query), replace: true };
  }
  if (to.path === '/labor-cost' || to.path.startsWith('/labor-cost/')) {
    const sections = laborCostSections(auth);
    if (!sections.length) return { name: 'dashboard' };
    if (!sections.some(section => section.id === to.meta.laborSection)) {
      return { ...laborCostLocation(sections[0].id, to.query), replace: true };
    }
  }
  if (to.name === 'counterparties' && !auth.hasPermission('budget.tab') && auth.hasPermission('legal.archive.view')) {
    return { name: 'legal-counterparties' };
  }
  if (!to.meta.public && to.meta.permission && !auth.hasPermission(to.meta.permission)) return { name: 'dashboard' };
  if (!to.meta.public && to.meta.permissions && !to.meta.permissions.some((permission) => auth.hasPermission(permission))) return { name: 'dashboard' };
  const legacySettings = { '/analytics/settings': to.query.focus === 'temperature' ? 'temperature' : 'analytics', '/analytics/temperature/settings': 'temperature', '/reviews/settings': 'reviews', '/inventory/settings': 'inventory' };
  if (legacySettings[to.path]) return { ...generalSettingsLocation(legacySettings[to.path]), replace: true };
  if (to.path === '/settings' && to.query.tab === 'general' && ['journals', 'schedule'].includes(to.query.section)) {
    return { path: '/' + to.query.section, query: to.query.section === 'schedule'
      ? { restaurantId: to.query.restaurantId, settings: 'positions' }
      : { journal: to.query.journal }, replace: true };
  }
  if (to.path === '/settings' && ['general', 'counterparties'].includes(to.query.tab) && !auth.isAdmin) {
    return { path: '/settings', query: { tab: 'profile' }, replace: true };
  }
  if (to.path === '/settings' && to.query.tab === 'counterparties') {
    return { path: '/counterparties', replace: true };
  }
  return true;
});

router.afterEach((to, from, failure) => {
  const lightForms = ['register', 'onboarding-public'];
  if (!failure && (lightForms.includes(to.name) || lightForms.includes(from.name))) applyRouteTheme(to.path);
});

installWorkspaceNavigation(router, workspaceMotion);

export default router;
